import {
  WorkspacePayload,
  Workspace,
  WorkspaceMemberPayload,
  WorkspaceMember,
  PendingInvitationPayload,
  PendingInvitation,
} from "./workspace.types";

/**
 * Transform pending invitation payload to UI format
 */
export const transformPendingInvitation = (
  invitation: PendingInvitationPayload
): PendingInvitation => ({
  invitationId: invitation.invitation_id,
  inviteeEmail: invitation.invitee_email || invitation.email || "",
  inviteeId: invitation.invitee_id,
  role: invitation.role,
  workspaceRole: invitation.workspace_role ?? null,
  allProjectAccess: invitation.all_project_access,
  projectIds: invitation.project_ids ?? [],
  status: invitation.status,
  expiresAt: invitation.expires_at,
  createdAt: invitation.created_at,
  displayName: invitation.display_name ?? undefined,
  positionTitle: invitation.position_title ?? undefined,
});

/**
 * Transform workspace payload to UI format
 */
export const transformWorkspace = (workspace: WorkspacePayload): Workspace => {
  const members = workspace.members?.map(transformWorkspaceMember) || [];

  // Derive pending invitations from members array (single source of truth)
  // Filter members with invitation_status === "pending" and convert to PendingInvitation format
  const pendingInvitations = members
    .filter((member) => member.invitationStatus === "pending")
    .map((member): PendingInvitation => {
      // Find the original payload to get invitation-specific fields
      const originalMember = workspace.members?.find((m) => m.id === member.id);
      return {
        invitationId: originalMember?.invitation_id || member.invitationId || "",
        inviteeEmail: member.email,
        inviteeId: member.id,
        role: "viewer", // Default project role - project role not available in members array
        workspaceRole: member.role as string,
        allProjectAccess: member.allProjectAccess,
        projectIds: member.projectIds,
        status: "pending",
        expiresAt: originalMember?.expires_at || "",
        createdAt: member.createdAt,
        displayName: member.displayName || null,
        positionTitle: member.positionTitle || null,
      };
    });

  return {
    id: workspace.id,
    name: workspace.name,
    imagePath: workspace.image_path,
    contributeBudgetBenchmarks: workspace.contribute_budget_benchmarks ?? true,
    createdAt: workspace.created_at,
    updatedAt: workspace.updated_at,
    members,
    memberCount:
      workspace.member_count ??
      members.filter((member) => member.invitationStatus !== "pending").length,
    pendingInvitations,
    permissions: workspace.permissions
      ? {
          permissions: workspace.permissions.permissions,
          role: workspace.permissions.role,
          workspaceId: workspace.permissions.workspace_id,
          userId: workspace.permissions.user_id,
        }
      : undefined,
  };
};

/**
 * Transform workspace array
 */
export const transformWorkspaceArray = (workspaces: WorkspacePayload[]): Workspace[] =>
  workspaces.map(transformWorkspace);

/**
 * Transform workspace member payload to UI format
 */
export const transformWorkspaceMember = (member: WorkspaceMemberPayload): WorkspaceMember => {
  // For pending invitations, use invitee_email if email is not available
  const email = member.email || member.invitee_email || "";
  const firstName = member.first_name || "";
  const lastName = member.last_name || "";
  const fullName = `${firstName} ${lastName}`.trim();
  // For pending invitations, use display_name if available, otherwise null (UI will show "—")
  // For active members, use fullName or email as fallback
  const displayName =
    member.invitation_status === "pending" ? member.display_name || null : fullName || email;

  return {
    id: member.id,
    role: member.role,
    firstName,
    lastName,
    email,
    positionTitle: member.position_title || "",
    companyName: member.company_name || "",
    profileImagePath: member.profile_image_path || undefined,
    isActive: member.is_active,
    verified: member.verified,
    createdAt: member.created_at,
    updatedAt: member.updated_at,
    lastLoginAt: member.last_login_at ?? null,
    fullName,
    displayName,
    allProjectAccess: member.all_project_access ?? false,
    projectIds: member.project_ids ?? [],
    invitationStatus: member.invitation_status || undefined,
    invitationId: member.invitation_id || undefined,
  };
};

/**
 * Transform workspace member array
 */
export const transformWorkspaceMemberArray = (
  members: WorkspaceMemberPayload[]
): WorkspaceMember[] => members.map(transformWorkspaceMember);

/**
 * Transform pending invitation array
 */
export const transformPendingInvitationArray = (
  invitations: PendingInvitationPayload[]
): PendingInvitation[] => invitations.map(transformPendingInvitation);
